// Chakra Imports
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  Switch,
  Text,
  useColorMode,
  useColorModeValue,
  useBreakpointValue,
  FormControl,
  FormLabel,
  Input,
  HStack,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { Separator } from "../Separator/Separator";
import React, { useState, useRef, useEffect } from "react";
import { getSignalColors, saveSignalColors, resetSignalColors, SignalColors, DEFAULT_COLORS } from "../../utils/signalColors";

interface ConfiguratorProps {
  secondary?: boolean;
  isOpen: boolean;
  onClose: () => void;
  fixed?: boolean;
  isChecked?: boolean;
  onSwitch?: (value: boolean) => void;
  onTransparent?: () => void;
  onOpaque?: () => void;
  [key: string]: any;
}

export default function Configurator(props: ConfiguratorProps) {
  const { secondary, isOpen, onClose, fixed, isChecked, onSwitch, onTransparent, onOpaque, ...rest } = props;

  const { colorMode, toggleColorMode } = useColorMode();
  const modalSize = useBreakpointValue({ base: "md", md: "lg" });

  // Brand colors
  const modalBg = useColorModeValue("white", "#151517");
  const primaryTextColor = useColorModeValue("#000000", "white");
  const secondaryTextColor = useColorModeValue("#575757", "gray.400"); // Glacial Grey
  const labelTextColor = useColorModeValue("#575757", "gray.400"); // Glacial Grey for labels
  const lightTextColor = useColorModeValue("#D3D3D3", "gray.500"); // Soft Glacial Grey
  const brandColor = "#38BBD1"; // Tropical Surf
  const brandGradient = "linear-gradient(135deg, #38BBD1, #3EEAEC)";

  const settingsRef = useRef<HTMLButtonElement>(null);

  // Signal color state
  const [signalColors, setSignalColors] = useState<SignalColors>(DEFAULT_COLORS);

  // Load colors from localStorage on mount and when drawer opens
  useEffect(() => {
    if (isOpen) {
      setSignalColors(getSignalColors());
    }
  }, [isOpen]);

  // Handle color changes
  const handleColorChange = (field: keyof SignalColors, value: string) => {
    const newColors = { ...signalColors, [field]: value };
    setSignalColors(newColors);
    saveSignalColors(newColors);
    // Trigger a custom event to notify components to update
    window.dispatchEvent(new CustomEvent('signalColorsChanged'));
  };

  // Reset to defaults
  const handleResetColors = () => {
    setSignalColors(DEFAULT_COLORS);
    resetSignalColors();
    window.dispatchEvent(new CustomEvent('signalColorsChanged'));
  };
  return (
    <>
      <style>
        {`
          [data-chakra-modal-content] {
            font-family: 'Urbanist', sans-serif !important;
          }
          [data-chakra-modal-content] * {
            font-family: 'Urbanist', sans-serif !important;
          }
          [data-chakra-modal-content] label,
          [data-chakra-modal-content] .chakra-form__label {
            font-family: 'IBM Plex Sans', sans-serif !important;
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={modalSize}
        finalFocusRef={settingsRef}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent 
          bg={modalBg} 
          maxW={{ base: "95%", md: "600px" }}
          borderRadius={{ base: "lg", md: "lg" }}
          m={{ base: 4, md: 0 }}
          data-chakra-modal-content="true"
          sx={{
            fontFamily: "'Urbanist', sans-serif !important",
            '*': {
              fontFamily: "'Urbanist', sans-serif !important"
            },
            'label, .chakra-form__label': {
              fontFamily: "'IBM Plex Sans', sans-serif !important"
            }
          }}
        >
        <ModalHeader 
          pt={{ base: "16px", md: "24px" }} 
          px={{ base: "16px", md: "24px" }} 
          pb={{ base: "12px", md: "16px" }} 
          sx={{ fontFamily: "'Urbanist', sans-serif" }}
        >
          <Text 
            fontSize="2xl" 
            fontWeight="500" 
            color={brandColor}
            lineHeight="1.2"
            mb="8px"
            sx={{
              fontFamily: "'Urbanist', sans-serif !important"
            }}
          >
              Dashboard Configurator
            </Text>
          <Text 
            fontSize="base" 
            mb="16px" 
            color={lightTextColor} 
            fontWeight="400"
            lineHeight="1.6"
            sx={{
              fontFamily: "'Urbanist', sans-serif !important"
            }}
          >
            Customize your dashboard settings.
            </Text>
            <Separator />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: "16px", md: "24px" }} pb={{ base: "16px", md: "24px" }}>
          <Tabs colorScheme="cyan" defaultIndex={0}>
            <TabList mb={6} borderColor="gray.200">
              <Tab 
                fontFamily="'IBM Plex Sans', sans-serif"
                fontSize="sm"
                fontWeight="500"
                color={secondaryTextColor}
                textTransform="uppercase"
                letterSpacing="0.05em"
                sx={{
                  fontFamily: "'IBM Plex Sans', sans-serif !important"
                }}
                _selected={{ 
                  color: brandColor, 
                  borderColor: brandColor,
                  fontWeight: "500"
                }}
                    _hover={{
                  color: brandColor
                    }}
                  >
                Appearance
              </Tab>
              <Tab 
                fontFamily="'IBM Plex Sans', sans-serif"
                fontSize="sm"
                fontWeight="500"
                color={secondaryTextColor}
                textTransform="uppercase"
                letterSpacing="0.05em"
                sx={{
                  fontFamily: "'IBM Plex Sans', sans-serif !important"
                }}
                _selected={{ 
                  color: brandColor, 
                  borderColor: brandColor,
                  fontWeight: "500"
                }}
                _hover={{
                  color: brandColor
                }}
                  >
                Signal Colors
              </Tab>
            </TabList>

            <TabPanels>
              {/* Appearance Tab */}
              <TabPanel px={0} pt={2}>
                <VStack spacing={6} align="stretch">
                  {/* Dark/Light Mode */}
                  <Box>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                    >
                      <Box>
                        <Text 
                          fontSize="lg" 
                          fontWeight="500" 
                          mb="4px" 
                          color={brandColor}
                          lineHeight="1.5"
                          sx={{
                            fontFamily: "'Urbanist', sans-serif !important"
                          }}
                        >
                          Dark/Light Mode
                        </Text>
                        <Text 
                          fontSize="sm" 
                          color={lightTextColor}
                          fontWeight="400"
                          lineHeight="1.5"
                          sx={{
                            fontFamily: "'Urbanist', sans-serif !important"
                          }}
                        >
                          Toggle between light and dark theme
                </Text>
                      </Box>
                      <Button 
                        onClick={toggleColorMode} 
                        bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                        color="white"
                        fontWeight="medium"
                        borderRadius="8px"
                        px="20px"
                        py="10px"
                        h="auto"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        textTransform="uppercase"
                        fontSize="sm"
                        sx={{
                          fontFamily: "'IBM Plex Sans', sans-serif !important"
                        }}
                        _hover={{
                          bgGradient: "linear-gradient(135deg, #3EEAEC, #38BBD1)",
                        }}
                        _active={{
                          bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                        }}
                      >
                        <Text fontSize="sm" color="white" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
                  Toggle {colorMode === "light" ? "Dark" : "Light"}
                        </Text>
                </Button>
              </Flex>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Signal Colors Tab */}
              <TabPanel px={0} pt={2}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Text 
                      fontSize="lg" 
                      fontWeight="500" 
                      color={brandColor} 
                      mb={2}
                      lineHeight="1.5"
                      sx={{
                        fontFamily: "'Urbanist', sans-serif !important"
                      }}
                    >
                  Signal Colors
                </Text>
                    <Text 
                      fontSize="sm" 
                      mb="16px" 
                      color={lightTextColor}
                      fontWeight="400"
                      lineHeight="1.5"
                      sx={{
                        fontFamily: "'Urbanist', sans-serif !important"
                      }}
                    >
                  Customize LONG and SHORT signal badge colors to avoid positive/negative connotations.
                </Text>
                  </Box>
                
                  {/* LONG Signal Colors */}
                  <Box>
                    <Text 
                      fontSize="base" 
                      fontWeight="500" 
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      mb={3}
                      lineHeight="1.5"
                      sx={{
                        fontFamily: "'Urbanist', sans-serif !important"
                      }}
                    >
                      LONG Signal
                    </Text>
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel 
                          fontSize="xs" 
                          color={labelTextColor} 
                          mb={1}
                          fontFamily="'IBM Plex Sans', sans-serif !important"
                          fontWeight="500"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                          sx={{
                            fontFamily: "'IBM Plex Sans', sans-serif !important"
                          }}
                        >
                          Start Color
                        </FormLabel>
                        <HStack>
                          <Input
                            type="color"
                            value={signalColors.longColor}
                            onChange={(e) => handleColorChange("longColor", e.target.value)}
                            w="60px"
                            h="32px"
                            p={1}
                            borderRadius="md"
                            cursor="pointer"
                          />
                          <Input
                            type="text"
                            value={signalColors.longColor}
                            onChange={(e) => handleColorChange("longColor", e.target.value)}
                            size="sm"
                            fontSize="xs"
                            fontFamily="monospace"
                            maxW="120px"
                          />
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel 
                          fontSize="xs" 
                          color={labelTextColor} 
                          mb={1}
                          fontFamily="'IBM Plex Sans', sans-serif !important"
                          fontWeight="500"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                          sx={{
                            fontFamily: "'IBM Plex Sans', sans-serif !important"
                          }}
                        >
                          End Color
                        </FormLabel>
                        <HStack>
                          <Input
                            type="color"
                            value={signalColors.longColorEnd}
                            onChange={(e) => handleColorChange("longColorEnd", e.target.value)}
                            w="60px"
                            h="32px"
                            p={1}
                            borderRadius="md"
                            cursor="pointer"
                          />
                          <Input
                            type="text"
                            value={signalColors.longColorEnd}
                            onChange={(e) => handleColorChange("longColorEnd", e.target.value)}
                            size="sm"
                            fontSize="xs"
                            fontFamily="monospace"
                            maxW="120px"
                          />
                        </HStack>
                      </FormControl>
                    </HStack>
                  </Box>

                  {/* SHORT Signal Colors */}
                  <Box>
                    <Text 
                      fontSize="base" 
                      fontWeight="500" 
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      mb={3}
                      lineHeight="1.5"
                      sx={{
                        fontFamily: "'Urbanist', sans-serif !important"
                      }}
                    >
                      SHORT Signal
                    </Text>
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel 
                          fontSize="xs" 
                          color={labelTextColor} 
                          mb={1}
                          fontFamily="'IBM Plex Sans', sans-serif !important"
                          fontWeight="500"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                          sx={{
                            fontFamily: "'IBM Plex Sans', sans-serif !important"
                          }}
                        >
                          Start Color
                        </FormLabel>
                        <HStack>
                          <Input
                            type="color"
                            value={signalColors.shortColor}
                            onChange={(e) => handleColorChange("shortColor", e.target.value)}
                            w="60px"
                            h="32px"
                            p={1}
                            borderRadius="md"
                            cursor="pointer"
                          />
                          <Input
                            type="text"
                            value={signalColors.shortColor}
                            onChange={(e) => handleColorChange("shortColor", e.target.value)}
                            size="sm"
                            fontSize="xs"
                            fontFamily="monospace"
                            maxW="120px"
                          />
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel 
                          fontSize="xs" 
                          color={labelTextColor} 
                          mb={1}
                          fontFamily="'IBM Plex Sans', sans-serif !important"
                          fontWeight="500"
                          textTransform="uppercase"
                          letterSpacing="0.05em"
                          sx={{
                            fontFamily: "'IBM Plex Sans', sans-serif !important"
                          }}
                        >
                          End Color
                        </FormLabel>
                        <HStack>
                          <Input
                            type="color"
                            value={signalColors.shortColorEnd}
                            onChange={(e) => handleColorChange("shortColorEnd", e.target.value)}
                            w="60px"
                            h="32px"
                            p={1}
                            borderRadius="md"
                            cursor="pointer"
                          />
                          <Input
                            type="text"
                            value={signalColors.shortColorEnd}
                            onChange={(e) => handleColorChange("shortColorEnd", e.target.value)}
                            size="sm"
                            fontSize="xs"
                            fontFamily="monospace"
                            maxW="120px"
                          />
                        </HStack>
                      </FormControl>
                    </HStack>
                  </Box>

                  {/* Reset Button */}
                  <Button
                    bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                    color="white"
                    fontWeight="medium"
                    borderRadius="8px"
                    px="20px"
                    py="10px"
                    h="auto"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                    fontSize="sm"
                    onClick={handleResetColors}
                    sx={{
                      fontFamily: "'IBM Plex Sans', sans-serif !important"
                    }}
                    _hover={{
                      bgGradient: "linear-gradient(135deg, #3EEAEC, #38BBD1)",
                    }}
                    _active={{
                      bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                    }}
                    alignSelf="flex-start"
                  >
                    <Text fontSize="sm" color="white" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
                    Reset to Defaults
                    </Text>
                  </Button>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
}

